/**
 * What every tooltip in the game shares: the rule that only one is open at a
 * time, the single document listener that closes it on a touch elsewhere, and
 * where on screen it goes.
 *
 * Before this, each tip added its own listener to the document -- 468 of them
 * with the glossary's technique list open, every one run on every touch.
 */

type Owner = { anchor: HTMLElement; close: () => void };

let owner: Owner | null = null;
let listening = false;

export const isTouchDevice =
  typeof document !== 'undefined' && 'ontouchstart' in document.documentElement;

function listen() {
  if (listening || typeof document === 'undefined') return;
  listening = true;

  // A touch anywhere but the open tip's own anchor closes it. A touch on the
  // anchor is left to the anchor, so tapping it again closes rather than
  // closing and reopening.
  document.addEventListener(
    'touchstart',
    (event) => {
      if (!owner) return;
      if (event.target instanceof Node && owner.anchor.contains(event.target)) return;
      owner.close();
    },
    { passive: true },
  );
}

/** Take the one open-tip slot, closing whichever tip held it. */
export function claim(next: Owner) {
  listen();
  const previous = owner;
  owner = next;
  if (previous && previous.close !== next.close) previous.close();
}

/** Give the slot back, if this tip still holds it. */
export function release(close: () => void) {
  if (owner?.close === close) owner = null;
}

/**
 * Where a tip goes: below its anchor where there is room, above where there is
 * not, and pulled in to stay on screen.
 *
 * The tip has to be measured first, hidden at the top left -- anywhere further
 * in, a fixed box can only grow as wide as the space to its right.
 */
export const measuringStyle = 'top: 0; left: 0; transform: none; visibility: hidden;';

export function placeTip(anchor: HTMLElement, tip: HTMLElement): string {
  const gap = 8;
  const margin = 8;
  const a = anchor.getBoundingClientRect();
  const t = tip.getBoundingClientRect();

  let top = a.bottom + gap;
  if (top + t.height > window.innerHeight - margin) top = a.top - gap - t.height;
  if (top < margin) top = Math.max(margin, (window.innerHeight - t.height) / 2);

  let left = a.left + a.width / 2 - t.width / 2;
  left = Math.min(Math.max(margin, left), window.innerWidth - t.width - margin);

  return `top: ${top}px; left: ${left}px; transform: none;`;
}
